import { computed, inject, Injectable, signal } from '@angular/core'
import { SmartQuoteCalculations } from '../smartquote-calculations.interface'
import { SmartQuote } from 'src/app/shared/interfaces/smartquote/smart-quote.interface'
import { SmartQuoteVatRate } from '../smartquote-vat-rate.interface'
import { CustomerService } from 'src/app/core/services/customer.service'
import { CompanySettingService } from 'src/app/core/services/company-setting.service'
import { ApplianceService } from 'src/app/core/services/appliance.service'
import { BehaviorSubject, catchError, of, tap } from 'rxjs'
import { Customer } from 'src/app/shared/interfaces/customer/customer.interface'
import { SmartQuoteService } from 'src/app/core/services/smartquote/smartquote.service'
import { Appointment } from 'src/app/shared/interfaces/appointment/appointment.interface'
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr'
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service'
import { Appliance } from 'src/app/shared/interfaces/appliances/appliance.interface'
import { Order } from 'src/app/shared/interfaces/orders/order.interface'
import { OrdersService } from 'src/app/core/services/orders/orders.service'
import { SmartQuoteTemplateService } from 'src/app/core/services/smartquote/smartquote-template.service'

@Injectable()
export class SmartquoteHelperService {
    #appointmentService = inject(AppointmentService)
    #confirmationService = inject(ConfirmationDialogService)
    #customerService = inject(CustomerService)
    #customerAppliancesService = inject(ApplianceService)
    #companySettingsService = inject(CompanySettingService)
    #orderService = inject(OrdersService)
    #router = inject(Router)
    #smartQuoteService = inject(SmartQuoteService)
    #toastr = inject(ToastrService)

    smartQuoteId = signal<number | null>(null)
    customerId = signal<number | null>(null)
    mainApplianceId = signal<number | null>(null)
    appointmentId = signal<number | null>(null)

    smartQuoteTemplates = toSignal(inject(SmartQuoteTemplateService).getSmartQuoteTemplates(), { initialValue: [] })

    #customerResource = rxResource({
        request: () => this.customerId(),
        loader: ({ request }) => {
            if (!request) {
                return of(null)
            }
            return this.#customerService.getCustomerById(request).pipe(
                tap((customer) => {
                    this.mainApplianceId.set(customer.mainApplianceId ?? null)
                })
            )
        },
    })
    customer = computed(() => this.#customerResource.value() || null)

    #mainApplianceResource = rxResource({
        request: () => this.mainApplianceId(),
        loader: ({ request }) => {
            if (!request) {
                return of(null)
            }
            return this.#customerAppliancesService.getCustomerApplianceById(request)
        },
    })
    mainAppliance = computed(() => this.#mainApplianceResource.value() || null)

    #smartQuoteResource = rxResource({
        request: () => this.smartQuoteId(),
        loader: ({ request }) => {
            if (!request) {
                return of(null)
            }
            return this.#smartQuoteService.getSmartQuoteById(request).pipe(
                tap((quote) => {
                    this.customerId.set(quote.customerId)
                    this.appointmentId.set(quote.appointmentId ?? null)

                    if (!quote.hasOrderBeenPlaced && quote.appointmentId) {
                        this.#toastr.info('An order has not been placed for this Quote.', 'Information', {
                            tapToDismiss: true,
                            extendedTimeOut: 20,
                        })
                    }
                }),
                catchError((error) => {
                    this.#toastr.error('Smartquote not found', 'Error')
                    this.#router.navigate(['/smartquotes'])
                    return of(null)
                })
            )
        },
    })
    smartquote = computed(() => this.#smartQuoteResource.value() || null)

    #appointmentResource = rxResource({
        request: () => this.appointmentId(),
        loader: ({ request }) => {
            if (!request) {
                return of(null)
            }
            return this.#appointmentService.getAppointmentById(request)
        },
    })
    appointment = computed(() => this.#appointmentResource.value() || null)

    #ordersResource = rxResource({
        request: () => this.smartQuoteId(),
        loader: ({ request }) => {
            if (!request) {
                return of(null)
            }
            return this.#orderService.getOrdersPaged({ pageNumber: 1, pageSize: 60, smartQuoteId: request })
        },
    })
    orders = computed(() => this.#ordersResource.value()?.items || [])

    companySettings = toSignal(this.#companySettingsService.getSettings(), { initialValue: null })

    createSmartquote(quote: SmartQuote): void {
        this.#smartQuoteService.createSmartquote(quote).subscribe({
            next: (quote) => {
                this.#toastr.success('Smartquote created', 'Success')
                this.#router.navigate(['/smartquote', quote.id])
            },
            error: () => {
                this.#toastr.error('Error creating smartquote', 'Error')
            },
        })
    }

    updateSmartquote(quote: SmartQuote): void {
        this.#smartQuoteService.updateSmartquote(this.smartquote()!.id, quote).subscribe({
            next: () => {
                this.#toastr.success('Smartquote updated', 'Success')
            },
            error: () => {
                this.#toastr.error('Error updating smartquote', 'Error')
            },
        })
    }

    markQuoteAsAccepted(): void {
        if (!this.smartquote()) {
            return
        }

        this.#confirmationService
            .confirm('Are you sure?', 'Are you sure you want to accept this quote?', 'Yes Accept Quote', 'Cancel')
            .then((confirmed) => {
                if (!confirmed) {
                    return
                }

                this.#smartQuoteService.acceptSmartquote(this.smartquote()!.id).subscribe({
                    next: () => {
                        this.#smartQuoteResource.reload()
                        this.#toastr.success('SmartQuote Accepted', 'Success')
                    },
                })
            })
    }

    markQuoteAsDeclined(): void {
        if (!this.smartquote()) {
            return
        }

        if (this.smartquote()?.declined) {
            this.#toastr.error('This quote has already been declined')
            return
        }

        this.#confirmationService
            .confirm('Are you sure?', 'Are you sure you want to decline this quote?', 'Yes Decline Quote', 'Cancel')
            .then((confirmed) => {
                if (!confirmed) {
                    return
                }

                this.#smartQuoteService.declineSmartquote(this.smartquote()!.id).subscribe({
                    next: () => {
                        this.#smartQuoteResource.reload()
                        this.#toastr.info('SmartQuote Declined', 'Confirmed')
                    },
                })
            })
    }

    copySmartquote(): void {
        if (!this.smartquote()) {
            return
        }

        this.#confirmationService
            .confirm('Are you sure?', 'Are you sure you want to copy this quote?', 'Yes copy the quote', 'Cancel')
            .then((confirmed) => {
                if (!confirmed) {
                    return
                }

                this.#smartQuoteService.copySmartquote(this.smartquote()!.id).subscribe({
                    next: (id) => {
                        this.#router.navigate([`/smartquote/${id}`])
                        this.smartQuoteId.set(id)

                        this.#toastr.info('SmartQuote copied', 'Success')
                    },
                })
            })
    }

    deleteSmartQuote(): void {
        if (!this.smartquote()) {
            return
        }

        this.#confirmationService
            .confirm('Are you sure?', 'Are you sure you want to delete this quote?', 'Yes DELETE Quote', 'Cancel')
            .then((confirmed) => {
                if (!confirmed) {
                    return
                }

                this.#smartQuoteService.deleteSmartQuote(this.smartquote()!.id).subscribe({
                    next: () => {
                        this.#router.navigate(['/smartquotes'])
                        this.#toastr.info('SmartQuote Deleted', 'Deleted')
                    },
                })
            })
    }

    discountPercentages = Array.from(Array(51).keys())
    depositPercentages = Array.from([...Array(10).keys()].map((x, i) => (i === 0 ? 0 : (x + 1) * 5)))
    vatPercentages: SmartQuoteVatRate[] = [
        { display: '0%', value: 0 },
        { display: '5%', value: 1.05 },
        { display: '20%', value: 1.2 },
    ]

    #statistics = signal<SmartQuoteCalculations>({
        depositTotal: 0,
        discountAsMoney: 0,
        discountAsPercentage: 0,
        labourCost: 0,
        linesTotal: 0,
        linesTermsTotal: 0,
        totalVat: 0,
        totalExcludeVat: 0,
        totalIncludeVat: 0,
    })

    statistics = this.#statistics.asReadonly()

    updateStats(data: SmartQuote) {
        let total = 0
        let linesTotal = 0
        let linesTermsTotal = 0

        data.lineItems.forEach((lineItem) => {
            const lineTotal = lineItem.cost * lineItem.quantity
            total += lineTotal
            linesTotal += lineTotal
            linesTermsTotal += lineItem.terms ? lineItem.terms * lineItem.quantity : 0
        })

        const labourCost = data.labourRate * data.labourHours
        total += labourCost

        const discountFromPercentage = data.discountPercentage ? (total / 100) * data.discountPercentage : 0
        total -= discountFromPercentage

        const moneyOff = data.moneyOff
        total -= moneyOff

        const totalExcludeVat = labourCost + linesTotal - moneyOff - discountFromPercentage
        const totalIncludeVat = totalExcludeVat * (data.vatMultiplier || 1)

        const result = {
            totalVat: totalIncludeVat - totalExcludeVat,
            totalIncludeVat: totalIncludeVat,
            totalExcludeVat: totalExcludeVat,
            linesTotal: linesTotal,
            linesTermsTotal: linesTermsTotal,
            labourCost: labourCost,
            discountAsPercentage: discountFromPercentage,
            discountAsMoney: moneyOff,
            depositTotal: (totalIncludeVat / 100) * data.depositPercentage,
        }

        this.#statistics.set(result)
    }
}
